/* ==========================================================================
   SBD cart drawer behaviour.

   Two jobs, both of which have to survive the theme rebuilding the drawer's
   contents on every cart change:

     1. the "You may also like" strip
     2. carrying a discount code through to checkout

   The free shipping progress bar that used to live here was removed on
   2026-09-30 while the threshold is under review. To bring it back, restore
   here: the money() cents formatter, renderShipping(), its three
   [data-sbd-ship*] lookups, the THRESHOLD read from SBD_CART and the
   renderShipping(cart) call in refresh(). Then the .sbd-cart__ship block and
   the three SBD_CART keys in snippets/cart-drawer.liquid, and the
   .sbd-cart__ship* rules in assets/sbd-cart-drawer.css.

   HOW CART CHANGES ARE DETECTED

   Impulse rewrites [data-products] and [data-subtotal] itself whenever the
   cart changes, but the event it fires for that is a theme internal and has
   changed name between versions. Rather than guess at one, this watches the
   subtotal element for mutations: whatever the theme calls the event, it
   always ends up writing a new subtotal, so that is the reliable signal.
   A cheap /cart.js read then gives the real numbers.

   Everything is defensive. If a fetch fails, the recommendations simply stay
   hidden and the drawer behaves exactly as the theme's own.
   ========================================================================== */

(function () {
  'use strict';

  var CFG = window.SBD_CART || {};
  var LIMIT = CFG.recsLimit || 3;
  var SECTION = 'sbd-cart-recommendations';

  var drawer = document.getElementById('CartDrawer');
  if (!drawer) return;

  var countEl = drawer.querySelector('[data-sbd-count]');
  var recsWrap = drawer.querySelector('[data-sbd-recs]');
  var recsBody = drawer.querySelector('[data-sbd-recs-body]');
  var subtotalEl = drawer.querySelector('[data-subtotal]');

  var lastRecsProductId = null;

  /* --------------------------------------------------------- item count */
  function renderCount(cart) {
    if (!countEl) return;
    var n = cart.item_count || 0;
    countEl.textContent = n ? ' (' + n + (n === 1 ? ' item)' : ' items)') : '';
  }

  /* ----------------------------------------------------- recommendations */
  /* Keyed off the first line item. Re-fetched only when that product
     changes, so adjusting a quantity does not re-request the same strip. */
  function renderRecs(cart) {
    if (!recsWrap || !recsBody) return;

    if (!cart.items || !cart.items.length) {
      recsWrap.hidden = true;
      lastRecsProductId = null;
      return;
    }

    var productId = cart.items[0].product_id;
    if (productId === lastRecsProductId) return;
    lastRecsProductId = productId;

    var url =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    url =
      url +
      'recommendations/products?section_id=' +
      SECTION +
      '&product_id=' +
      productId +
      '&limit=' +
      LIMIT;

    fetch(url)
      .then(function (r) {
        return r.ok ? r.text() : Promise.reject(r.status);
      })
      .then(function (html) {
        // Shopify wraps section renders in a div; take the inner markup.
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var grid = holder.querySelector('.sbd-cart__recs-grid');

        if (!grid || !grid.children.length) {
          recsWrap.hidden = true;
          return;
        }
        recsBody.innerHTML = '';
        recsBody.appendChild(grid);
        recsWrap.hidden = false;
      })
      .catch(function () {
        recsWrap.hidden = true;
      });
  }

  /* ------------------------------------------------------------- refresh */
  var refreshing = false;
  function refresh() {
    if (refreshing) return;
    refreshing = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : Promise.reject(r.status);
      })
      .then(function (cart) {
        renderCount(cart);
        renderRecs(cart);
      })
      .catch(function () {
        /* leave the drawer as the theme rendered it */
      })
      .then(function () {
        refreshing = false;
      });
  }

  /* --------------------------------------------------------- discount code */
  /* The toggle, and the hand-off to Shopify's own /discount/ route. */
  var toggle = drawer.querySelector('[data-sbd-discount-toggle]');
  var panel = drawer.querySelector('[data-sbd-discount-panel]');
  var input = drawer.querySelector('[data-sbd-discount-input]');

  if (toggle && panel) {
    toggle.addEventListener('click', function () {
      var open = panel.hidden;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open && input) input.focus();
    });
  }

  var form = document.getElementById('CartDrawerForm');
  if (form && input) {
    form.addEventListener(
      'submit',
      function (e) {
        var code = (input.value || '').trim();
        if (!code) return; // no code, let the theme submit as normal

        // Terms checkbox, if the theme is enforcing one, still has to pass.
        var terms = drawer.querySelector('.cart__terms-checkbox');
        var btn = drawer.querySelector('[name="checkout"]');
        if (
          terms &&
          btn &&
          btn.getAttribute('data-terms-required') === 'true' &&
          !terms.checked
        ) {
          return;
        }

        e.preventDefault();
        var root =
          window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root
            : '/';
        window.location.href =
          root + 'discount/' + encodeURIComponent(code) + '?redirect=/checkout';
      },
      // Capture, so this runs before the theme's own submit handler.
      true
    );
  }

  /* ----------------------------------------------------------- wiring up */
  refresh();

  if (subtotalEl && 'MutationObserver' in window) {
    new MutationObserver(refresh).observe(subtotalEl, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  // Opening the drawer is also a good moment to be current, in case the cart
  // was changed in another tab.
  if ('MutationObserver' in window) {
    new MutationObserver(function () {
      if (drawer.classList.contains('is-open')) refresh();
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  // Belt and braces: if the theme does fire a cart event, take it too.
  ['cart:updated', 'cart:build', 'cart:refresh'].forEach(function (name) {
    document.addEventListener(name, refresh);
  });
})();
